import { FC, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Tag from '../Shared/Tag';

interface TimelineItem {
  day: number;
  title: string;
  description: string;
}

interface TimelineItemProps extends TimelineItem {
  isActive: boolean;
  onClick: () => void;
}

interface TimelineSectionProps {
  timelineRef: React.RefObject<HTMLDivElement>;
  customTitle?: string;
  customDescription?: string;
  customItems?: TimelineItem[];
  containerClassName?: string;
}

const VerticalLabel: FC<Omit<TimelineItemProps, 'description'>> = ({
  day,
  title,
  isActive,
  onClick,
}) => {
  return (
    <motion.div
      className="absolute inset-0 cursor-pointer"
      onClick={onClick}
      initial={{ opacity: 0 }}
      animate={{ opacity: isActive ? 0 : 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
    >
      {/* This div provides the left and right borders */}
      <div className="absolute inset-0 border-x border-zinc-300 dark:border-white/20">
        <div
          className="absolute top-[40px] left-1/2 -translate-x-1/2 h-[calc(100%-40px)] flex overflow-visible"
          style={{ writingMode: 'vertical-lr' }}
        >
          <div className="flex items-center gap-3">
            <Tag size="sm">DAY {day}</Tag>
            <span className="text-black dark:text-white font-light text-xl whitespace-nowrap">
              {title}
            </span>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

const ContentCard: FC<TimelineItemProps> = ({
  day,
  title,
  description,
  isActive,
}) => {
  return (
    <motion.div
      className="absolute inset-0"
      initial={{ opacity: 0 }}
      animate={{ opacity: isActive ? 1 : 0, scale: isActive ? 1 : 0.95 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
    >
      {/* Add Tag inside card */}
      <div className="border border-black dark:border-white rounded-[32px] p-8 h-full min-h-[320px] overflow-hidden">
        <Tag size="sm" className="mb-4">
          DAY {day}
        </Tag>
        <h3 className="text-[24px] text-black dark:text-white font-normal tracking-tight mb-6">
          {title}
        </h3>
        <p className="text-zinc-600 dark:text-white/70 text-base leading-relaxed">
          {description}
        </p>
      </div>
    </motion.div>
  );
};

const TimelineItem: FC<TimelineItemProps> = (props) => {
  const { isActive, onClick } = props;

  return (
    <motion.div
      className={`relative transition-all duration-500 ease-in-out ${
        isActive ? 'flex-[1.15]' : 'flex-[0_0_80px]'
      }`}
      onClick={onClick}
    >
      {/* Timeline dot */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2">
        <motion.div
          className={`w-2 h-2 rounded-full ${isActive ? 'bg-black dark:bg-white' : 'bg-zinc-300 dark:bg-white/30'}`}
          initial={{ scale: 0 }}
          whileInView={{ scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.4 }}
        />
      </div>

      {/* Content container */}
      <div className="mt-8 min-h-[320px] relative overflow-hidden">
        <AnimatePresence mode="wait">
          {isActive ? (
            <ContentCard key="card" {...props} />
          ) : (
            <VerticalLabel key="label" {...props} />
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
};

const defaultTimelineItems = [
  {
    day: 1,
    title: 'Kickoff Call (60 minutes)',
    description:
      'Meet the core team, align on goals, and lock scope so we can start building the same day.',
  },
  {
    day: 2,
    title: 'Subscription Live',
    description:
      'Your workspace spins up, dedicated channel opens, and priorities are queued for delivery.',
  },
  {
    day: 4,
    title: 'Parallel Build Sprint',
    description:
      'Strategy keeps moving while your delivery squad assembles assets and engineering paths in tandem.',
  },
  {
    day: 8,
    title: 'First Value Drop',
    description:
      'Review-ready deliverables land with clear context, next steps, and the crew ready for live feedback.',
  },
  {
    day: 14,
    title: 'Scale On Signal',
    description:
      'Dial velocity up or down the moment you need it—adding power-ups or specialists without friction.',
  },
];

const TimelineSection: FC<TimelineSectionProps> = ({
  timelineRef,
  customTitle = 'We deliver as fast as you can review.',
  customDescription = 'From initial chat to value delivery starting in just 8 days, with flexible scaling thereafter.',
  customItems,
  containerClassName,
}) => {
  const [activeIndex, setActiveIndex] = useState(0);
  
  const timelineItems = customItems || defaultTimelineItems;

  return (
    <section
      ref={timelineRef}
      className={containerClassName || 'py-32 bg-white dark:bg-black'}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mb-12 md:mb-20"
        >
          <div className="inline-block px-4 py-1 rounded-full bg-zinc-100 dark:bg-white/5 backdrop-blur-sm text-xs font-medium tracking-[0.2em] text-zinc-600 dark:text-white/60 mb-6 md:mb-8">
            PROCESS
          </div>
          <h2 className="text-[clamp(32px,7vw,56px)] leading-tight text-black dark:text-white max-w-3xl mb-5 md:mb-6">
            {customTitle}
          </h2>
          <p className="text-base sm:text-lg md:text-xl text-zinc-600 dark:text-white/70 max-w-2xl">
            {customDescription}
          </p>
        </motion.div>

        <div className="flex flex-col gap-4 md:hidden">
          {timelineItems.map((item, index) => (
            <div
              key={`mobile-timeline-${item.day}-${index}`}
              className="rounded-[28px] border border-zinc-200 bg-white/95 px-5 py-6 shadow-sm dark:border-zinc-800 dark:bg-zinc-900/90"
            >
              <Tag size="sm" className="mb-3">
                DAY {item.day}
              </Tag>
              <h3 className="text-xl font-medium text-zinc-900 dark:text-white mb-2">
                {item.title}
              </h3>
              <p className="text-sm leading-relaxed text-zinc-600 dark:text-white/70">
                {item.description}
              </p>
            </div>
          ))}
        </div>

        {/* Timeline container */}
        <div className="relative hidden md:block">
          {/* Horizontal line */}
          <div className="absolute top-[5px] left-0 right-0 h-[2px] bg-zinc-200 dark:bg-white/10" />

          {/* Timeline items - Changed to flex */}
          <div className="flex gap-4 min-h-[360px]">
            {timelineItems.map((item, index) => (
              <TimelineItem
                key={index}
                {...item}
                isActive={index === activeIndex}
                onClick={() => setActiveIndex(index)}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default TimelineSection;
